import { motion } from "framer-motion";
import { Check, Clock3 } from "lucide-react";

const sharedFeatures = [
  "EHR + AI Scribe",
  "Practice Management & Scheduling",
  "RCM",
  "HRMS",
  "Patient Engagement",
  "Tele Health",
  "Secure Chat",
  "Calls",
  "Fax",
  "Document Management",
  "Analytics Reporting",
  "Meaningful Use Dashboards",
  "24x7 Support",
];

const upcomingFeatures = [
  "ePrescribing and Formulary Checking",
  "Referral Management",
  "P2P Services",
  "Astra Mobile",
];

const plans = [
  { name: "Solo", price: "$249", unit: "/ provider / mo", providers: "1 provider", desc: "For independent providers running a full practice.", cta: "Start Free Demo", featured: false },
  { name: "Group", price: "$199", unit: "/ provider / mo", providers: "2+ providers", desc: "For multi-provider practices - the best value per seat.", cta: "Get a Demo", featured: true },
];

export const Pricing = ({ onSelect }) => {
  return (
    <section id="pricing" data-testid="pricing" className="relative z-10 bg-[#F2F8FC] py-16 md:py-24 border-t border-[#D8E6F1]">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2880B8] mb-5">/ Software Pricing</p>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-[#0C2A40] leading-[1.02]">
            One platform. <span className="font-extrabold text-[#2880B8]">Every feature included.</span>
          </h2>
          <p className="mt-6 text-[#51667A] text-lg leading-relaxed">
            Every plan includes the complete Astra platform. No setup costs, no feature gates, no hidden fees.
          </p>
          <div className="mt-6 inline-flex rounded-full bg-white border border-[#D8E6F1] px-5 py-2 font-mono text-[11px] tracking-[0.16em] uppercase text-[#2880B8]">
            No Setup Costs
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-6xl mx-auto">
          {plans.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
              className={`relative rounded-2xl border p-8 md:p-9 flex flex-col ${
                t.featured
                  ? "border-[#2880B8] bg-[#0C2A40] text-white shadow-[0_40px_80px_-30px_rgba(40,128,184,0.5)]"
                  : "border-[#D8E6F1] bg-white"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-8 bg-[#2880B8] text-white font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full">
                  Best Value
                </span>
              )}
              <h3 className={`font-display text-2xl font-bold tracking-tight ${t.featured ? "text-white" : "text-[#0C2A40]"}`}>{t.name}</h3>
              <span className={`mt-1 font-mono text-[11px] tracking-[0.12em] uppercase ${t.featured ? "text-[#9FD0EC]" : "text-[#2880B8]"}`}>{t.providers}</span>
              <div className="mt-4 flex items-baseline gap-2">
                <span className={`font-mono text-4xl font-bold ${t.featured ? "text-white" : "text-[#0C2A40]"}`}>{t.price}</span>
                <span className={`font-mono text-[11px] tracking-[0.1em] uppercase ${t.featured ? "text-[#9FD0EC]" : "text-[#93A5B5]"}`}>{t.unit}</span>
              </div>
              <p className={`mt-4 text-sm leading-relaxed ${t.featured ? "text-[#A9C4DD]" : "text-[#51667A]"}`}>{t.desc}</p>

              <div className={`mt-7 pt-7 border-t ${t.featured ? "border-white/15" : "border-[#D8E6F1]"}`}>
                <p className={`font-mono text-[10px] tracking-[0.15em] uppercase mb-4 ${t.featured ? "text-[#9FD0EC]" : "text-[#93A5B5]"}`}>Everything included</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {sharedFeatures.map((f) => (
                    <li key={f} className={`flex items-start gap-3 text-sm ${t.featured ? "text-white/90" : "text-[#0C2A40]"}`}>
                      <Check size={16} className={`${t.featured ? "text-[#9FD0EC]" : "text-[#2880B8]"} mt-0.5 shrink-0`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className={`mt-7 pt-7 border-t ${t.featured ? "border-white/15" : "border-[#D8E6F1]"}`}>
                <p className={`font-mono text-[10px] tracking-[0.15em] uppercase mb-4 ${t.featured ? "text-[#9FD0EC]" : "text-[#93A5B5]"}`}>Upcoming in future</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {upcomingFeatures.map((f) => (
                    <li key={f} className={`flex items-start gap-3 text-sm ${t.featured ? "text-white/75" : "text-[#51667A]"}`}>
                      <Clock3 size={15} className={`${t.featured ? "text-[#9FD0EC]" : "text-[#2880B8]"} mt-0.5 shrink-0`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                onClick={() => onSelect?.(`${t.name} plan`)}
                className={`mt-8 w-full py-4 rounded-full font-mono text-[11px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                  t.featured
                    ? "bg-[#2880B8] text-white hover:bg-[#1F6EA1]"
                    : "border border-[#D8E6F1] text-[#0C2A40] hover:border-[#2880B8] hover:text-[#2880B8]"
                }`}
              >
                {t.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
